import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Menu } from 'types/menu';
import { Meta } from 'types/meta';
import { FullScreen } from 'styles/FullScreen';
import { Head } from 'common/components/Head';
import { Grid, makeStyles, MenuItem, Select } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { getTranslation } from 'localization/helpers';
import { SvgImage } from 'common/components/SvgImage';
import { Company } from 'types/company';
import { Picture } from 'common/components/Picture';
import LanguageSwitcher from 'common/components/LanguageSwitcher';
import backArrow from 'images/arrowLeftIcon.svg';
import { MenuItemTranslations } from 'types/menuItem';

const BackArrowImage = styled(SvgImage)`
  display: block;
`;

const BackButtonContainer = styled.div`
  cursor: pointer;
  padding: 15px 20px 15px 20px;
`;

const MenuImage = styled(Picture)`
  width: 100%;
  display: block;
`;

const Frame = styled.iframe`
  height: 100%;
  width: 100% !important;
  border: none;
  display: block;
`;

export const FileMenu: FC<{ menu: Menu; company: Company; meta: Meta }> = ({
  menu,
  company,
  meta
}) => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const [selectedMenuIndex, setSelectedMenuIndex] = useState(0);

  const title = getTranslation(
    company.translations,
    'title',
    i18n.language,
    meta.default_locale
  );

  const localizedMenu = menu.menu_items.map(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    item => item.translations![i18n.language]
  );

  const renderMenu = () => {
    const menuItem = localizedMenu[selectedMenuIndex];
    const url =
      'https://drive.google.com/viewerng/viewer?embedded=true&url=' +
      API_URL +
      menuItem.url +
      '#toolbar=0&rm=minimal';
    if (typeof menuItem.url === 'string') {
      return <Frame src={url} />;
    } else {
      const url = menuItem.url;
      return url.src.map((p, index) => (
        <MenuImage
          source={{
            src: `${API_URL}${p}`,
            fallback: `${API_URL}${url.fallback[index]}`
          }}
          key={index}
        />
      ));
    }
  };

  const metaTitle = `${t('menu')} ${title}`;
  const metaDescription = `${t('youCanSeeMenuHere')} ${title} ${t(
    'viaRevizo'
  )}`;
  return (
    <FullScreen
      style={{
        flexDirection: 'column',
        justifyContent: 'start',
        backgroundColor: meta.color,
        width: '100%'
      }}
    >
      <Head title={metaTitle} description={metaDescription} />
      <Grid
        container
        direction="row"
        style={{ minHeight: 60 }}
        alignItems="center"
      >
        <Grid xs={2}>
          <Link to="..">
            <BackButtonContainer>
              <BackArrowImage color="white" source={backArrow} />
            </BackButtonContainer>
          </Link>
        </Grid>
        <Grid
          xs={8}
          container
          direction="row"
          justify="center"
          style={{ paddingLeft: 20, paddingRight: 20 }}
        >
          <Select
            classes={{
              root: classes.selectorRoot,
              select: classes.selectorRoot
            }}
            defaultValue={0}
            onChange={e => {
              if (typeof e.target.value === 'number')
                setSelectedMenuIndex(e.target.value);
            }}
            onClose={() => {
              setTimeout(() => {
                if (document.activeElement instanceof HTMLElement)
                  document.activeElement.blur();
              }, 0);
            }}
            placeholder={t('chooseMenu')}
            className={classes.dropdown}
          >
            {localizedMenu.map((menu: MenuItemTranslations, index: number) => (
              <MenuItem key={index} value={index}>
                {menu.title}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid xs={2}>
          <LanguageSwitcher companyLanguages={meta.locales} />
        </Grid>
      </Grid>
      {renderMenu()}
    </FullScreen>
  );
};

const useStyles = makeStyles(() => ({
  dropdown: {
    width: '100%',
    maxWidth: '300px'
  },
  selectorRoot: {
    border: '0px !important',
    backgroundColor: 'white !important',
    fontFamily: 'Montserrat, sans-serif !important;',
    fontSize: '13px',
    paddingLeft: 10,
    position: 'relative',
    '& .MuiInputBase-input:focus': {
      border: '0px !important'
    }
  }
}));
